import React from "react";
import { Controller, useFormContext } from "react-hook-form";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import MuiTextField from "@mui/material/TextField";

import { FormField } from "components/form/FormField/FormField";
import { COUNTRY_OPTIONS } from "utils/countries";

import style from "../AddVaccineForm.module.css";

const filterIsoOptions = createFilterOptions();

// Country picker: ISO 3166 options (Ireland pinned first), autocomplete search.
// A pre-existing value that isn't an ISO code is still shown as the current
// selection (so legacy data isn't lost), but it is NOT offered in the dropdown —
// picking any ISO country replaces it.
export const CountrySelectField = ({
  name,
  label,
  placeholder = "Search country...",
  disabled = false,
  required = false,
  ariaRequired = false,
}) => {
  const { control } = useFormContext();

  return (
    <FormField name={name}>
      <label className={style.pregnancyFormLabel}>
        {label}
        {required ? " *" : ""}
      </label>
      <Controller
        name={name}
        control={control}
        render={({ field }) => {
          const matched = COUNTRY_OPTIONS.find(
            (country) => country.value === field.value
          );
          const legacyOption =
            !matched && field.value
              ? { value: field.value, label: field.value, isLegacy: true }
              : null;
          const options = legacyOption
            ? [legacyOption, ...COUNTRY_OPTIONS]
            : COUNTRY_OPTIONS;

          return (
            <Autocomplete
              disabled={disabled}
              options={options}
              autoHighlight
              value={matched || legacyOption || null}
              getOptionLabel={(option) =>
                typeof option === "string" ? option : option.label || ""
              }
              onChange={(_, newValue) =>
                field.onChange(newValue ? newValue.value : "")
              }
              isOptionEqualToValue={(option, value) =>
                option.value === value?.value
              }
              // Keep the legacy (non-ISO) option out of the suggestion list.
              filterOptions={(opts, state) =>
                filterIsoOptions(
                  opts.filter((opt) => !opt.isLegacy),
                  state
                )
              }
              renderInput={(params) => (
                <MuiTextField
                  {...params}
                  placeholder={placeholder}
                  inputProps={{
                    ...params.inputProps,
                    "aria-required":
                      required || ariaRequired ? "true" : undefined,
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      height: "48px",
                      borderRadius: "12px !important",
                      backgroundColor: disabled ? "#eeeef0" : "#fdfdfd",
                      fontFamily: "'Nunito', sans-serif",
                      fontSize: "16px",
                      boxShadow: "0px 1px 2px 0px rgba(0, 0, 0, 0.05)",
                      border: disabled
                        ? "1px solid #b7b8c2"
                        : "1px solid #8f8fa1",
                      "& fieldset": {
                        border: "none !important",
                      },
                      "&:hover": {
                        border: disabled
                          ? "1px solid #b7b8c2"
                          : "1px solid #8f8fa1",
                      },
                      "&.Mui-focused": {
                        border: "1px solid #006546",
                      },
                      "& .MuiAutocomplete-input": {
                        padding: "0 !important",
                        paddingLeft: "8px !important",
                      },
                    },
                    "& .MuiInputBase-input": {
                      fontFamily: "'Nunito', sans-serif",
                      fontSize: "16px",
                      color: disabled ? "#717186" : "#26252c",
                    },
                    "& .MuiInputBase-input::placeholder": {
                      color: "#8f8fa1",
                      opacity: 1,
                    },
                  }}
                />
              )}
              sx={{
                "& .MuiAutocomplete-popupIndicator": { color: "#26252c" },
                "& .MuiAutocomplete-clearIndicator": { color: "#717186" },
              }}
              slotProps={{
                paper: {
                  sx: {
                    borderRadius: "12px",
                    marginTop: "4px",
                    boxShadow: "0px 4px 16px rgba(0, 0, 0, 0.15)",
                    "& .MuiAutocomplete-listbox": {
                      fontFamily: "'Nunito', sans-serif",
                      fontSize: "16px",
                      "& .MuiAutocomplete-option": {
                        padding: "12px 16px",
                        "&:hover": { backgroundColor: "#f7f7f8" },
                        "&.Mui-focused": { backgroundColor: "#f7f7f8" },
                        '&[aria-selected="true"]': {
                          backgroundColor: "#e7efeb",
                        },
                      },
                    },
                  },
                },
              }}
            />
          );
        }}
      />
    </FormField>
  );
};
